import React from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type PricingTier = {
  name: string;
  price: string;
  pricePeriod: string;
  features: string[];
};

type TitleStyle = {
  text?: string;
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  fontSize?: string;
  align?: string;
  transform?: string;
  color?: string;
  accentColor?: string;
};

type CardTypoStyle = {
  tierNameFontFamily?: string;
  tierNameFontWeight?: string;
  tierNameFontStyle?: string;
  tierNameTransform?: string;
  priceFontFamily?: string;
  priceFontWeight?: string;
  priceFontStyle?: string;
  featureFontFamily?: string;
  featureFontWeight?: string;
  featureFontStyle?: string;
  buttonFontFamily?: string;
  buttonFontWeight?: string;
  buttonTransform?: string;
};

type PricingTableProps = {
  pricingTiers: PricingTier[];
  productName: string;
  onSelectPlan: (tier: PricingTier) => void;
  isSubmitting?: boolean;
  isEmbedded?: boolean;
  titleStyle?: TitleStyle;
  cardTypography?: CardTypoStyle;
};

export function PricingTable({
  pricingTiers,
  productName,
  onSelectPlan,
  isSubmitting = false,
  isEmbedded = window.self !== window.top,
  titleStyle,
  cardTypography
}: PricingTableProps) {
  if (!pricingTiers || pricingTiers.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No pricing tiers available</p>
      </div>
    );
  }

  const fontFamilyMap: Record<string, string> = {
    sans: "ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
    serif: "ui-serif, Georgia, Cambria, 'Times New Roman', serif",
    mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
    display: "Georgia, 'Times New Roman', serif",
  };
  const sizeMap: Record<string, string> = {
    sm: "1.125rem",
    base: "1.5rem",
    lg: "1.875rem",
    xl: "2.25rem",
    "2xl": "3rem",
  };
  const titleText = titleStyle?.text ?? "Choose your plan for";
  const titleFontFamily = fontFamilyMap[titleStyle?.fontFamily || "sans"] || fontFamilyMap.sans;
  const titleFontSize = sizeMap[titleStyle?.fontSize || "base"] || sizeMap.base;
  const titleAlign = (titleStyle?.align as "left" | "center" | "right") || "center";
  const titleTransform = (titleStyle?.transform as "none" | "uppercase" | "capitalize") || "none";
  const titleFontWeight = titleStyle?.fontWeight || "700";
  const titleFontStyle = titleStyle?.fontStyle || "normal";
  const titleColor = titleStyle?.color || "#111827";
  const titleAccent = titleStyle?.accentColor || "#4f46e5";
  const titleTracking = titleStyle?.fontFamily === "display" ? "-0.02em" : undefined;

  const fam = (k?: string) => fontFamilyMap[k || "sans"] || fontFamilyMap.sans;
  const tierNameStyle: React.CSSProperties = {
    fontFamily: fam(cardTypography?.tierNameFontFamily),
    fontWeight: (cardTypography?.tierNameFontWeight || "700") as any,
    fontStyle: cardTypography?.tierNameFontStyle || "normal",
    textTransform: (cardTypography?.tierNameTransform as any) || "none",
    letterSpacing: cardTypography?.tierNameFontFamily === "display" ? "-0.01em" : undefined,
  };
  const priceStyle: React.CSSProperties = {
    fontFamily: fam(cardTypography?.priceFontFamily),
    fontWeight: (cardTypography?.priceFontWeight || "700") as any,
    fontStyle: cardTypography?.priceFontStyle || "normal",
    letterSpacing: cardTypography?.priceFontFamily === "display" ? "-0.02em" : undefined,
  };
  const featureStyle: React.CSSProperties = {
    fontFamily: fam(cardTypography?.featureFontFamily),
    fontWeight: (cardTypography?.featureFontWeight || "400") as any,
    fontStyle: cardTypography?.featureFontStyle || "normal",
  };
  const buttonStyle: React.CSSProperties = {
    fontFamily: fam(cardTypography?.buttonFontFamily),
    fontWeight: (cardTypography?.buttonFontWeight || "500") as any,
    textTransform: (cardTypography?.buttonTransform as any) || "none",
  };

  return (
    <div className="w-full">
      {!isEmbedded && (
        <h2
          className="mb-8"
          style={{
            fontFamily: titleFontFamily,
            fontSize: titleFontSize,
            fontWeight: titleFontWeight as any,
            fontStyle: titleFontStyle,
            textAlign: titleAlign,
            textTransform: titleTransform,
            letterSpacing: titleTracking,
            color: titleColor,
          }}
        >
          {titleText}{titleText ? " " : ""}
          <span style={{ color: titleAccent }}>{productName}</span>
        </h2>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pricingTiers.map((tier, index) => (
          <Card 
            key={index} 
            className={`overflow-hidden transition-all hover:shadow-lg ${
              index === 1 ? "border-indigo-200 shadow-md ring-1 ring-indigo-200" : ""
            }`}
          >
            <CardHeader className="p-6 bg-gradient-to-br from-slate-50 to-slate-100">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl" style={tierNameStyle}>{tier.name}</h3>
                  {index === 1 && (
                    <Badge className="bg-indigo-500 hover:bg-indigo-600">Most Popular</Badge>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-3xl" style={priceStyle}>${tier.price}</div>
                  <div className="text-sm text-muted-foreground" style={{ fontFamily: priceStyle.fontFamily }}>
                    per {tier.pricePeriod === "month" ? "month" : "year"}
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              <ul className="space-y-3">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span style={featureStyle}>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>

            <CardFooter className="p-6 pt-0">
              <Button
                onClick={() => onSelectPlan(tier)}
                className={`w-full ${
                  index === 1
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                    : "bg-indigo-100 hover:bg-indigo-200 text-indigo-700"
                }`}
                style={buttonStyle}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : `Choose ${tier.name}`}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
} 